import { ClockCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { DateTime } from 'luxon';

import {
    ShiftListItemDto,
    ShiftTimeWorkedDto,
    ShiftTimeWorkedDtoTypeEnum,
} from '@clh/api-client';
import { STANDARD_MEAL_BREAK_MINUTES } from '@clh/util';

import { TimeWorkedSummary } from './time-worked-summary';

const timeWorkedTypeLabels: Record<ShiftTimeWorkedDtoTypeEnum, string> = {
    [ShiftTimeWorkedDtoTypeEnum.Standard]: 'Regular',
    [ShiftTimeWorkedDtoTypeEnum.OnCall]: 'On Call',
    [ShiftTimeWorkedDtoTypeEnum.CalledIn]: 'Called In',
};

function formatTime(date: Date, timezone?: string | null) {
    return DateTime.fromJSDate(
        date,
        timezone ? { zone: timezone } : undefined
    ).toFormat('HHmm ZZZZ');
}

function ClockInOutTooltip({
    timeWorked,
    timezone,
}: {
    timeWorked: ShiftTimeWorkedDto[];
    timezone?: string | null;
}) {
    const sorted = [...timeWorked].sort(
        (a, b) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );

    const content = (
        <div>
            {sorted.map((tw) => (
                <div key={tw.id}>
                    {formatTime(tw.startTime, timezone)} –{' '}
                    {formatTime(tw.endTime, timezone)}
                    <em> ({timeWorkedTypeLabels[tw.type] ?? tw.type})</em>
                </div>
            ))}
        </div>
    );

    return (
        <Tooltip title={content}>
            <ClockCircleOutlined style={{ marginLeft: 8, cursor: 'pointer' }} />
        </Tooltip>
    );
}

export const SubmittedTimeSummary = ({
    selection,
}: {
    selection: ShiftListItemDto;
}) => {
    const netWorkedMinutes = selection.minutesWorked ?? 0;
    const tookMealBreak =
        netWorkedMinutes > 0 &&
        selection.skippedMealBreak !== null &&
        !selection.skippedMealBreak;
    // shift.minutesWorked is NET (meal break already subtracted).
    // TimeWorkedSummary expects GROSS hours and subtracts meal break itself,
    // so we add it back here to avoid double subtraction.
    const mealBreakMinutes = tookMealBreak ? STANDARD_MEAL_BREAK_MINUTES : 0;
    const worked = (netWorkedMinutes + mealBreakMinutes) / 60;
    const onCall = selection.onCallShift?.minutesWorked
        ? selection.onCallShift.minutesWorked / 60
        : 0;

    const timezone = selection.facility.address.timezone;
    const hasTimeSubmitted = !!(selection.minutesWorked || onCall);
    const showMealBreakRow =
        netWorkedMinutes > 0 && selection.skippedMealBreak !== null;

    if (!hasTimeSubmitted && !selection.didNotWork) {
        return <em>No time submitted yet.</em>;
    }

    return (
        <div>
            {selection.didNotWork && <em>Nurse did not work this shift</em>}
            {!!selection.timeWorked?.length && hasTimeSubmitted && (
                <span>
                    Clock in/out details
                    <ClockInOutTooltip
                        timeWorked={selection.timeWorked}
                        timezone={timezone}
                    />
                </span>
            )}
            {hasTimeSubmitted && (
                <>
                    <br />
                    <TimeWorkedSummary
                        didNotWork={selection.didNotWork}
                        worked={worked}
                        onCall={onCall}
                        tookMealBreak={tookMealBreak}
                    />
                    {showMealBreakRow && (
                        <div style={{ marginTop: 8 }}>
                            Meal break:{' '}
                            <b>{selection.skippedMealBreak ? 'No' : 'Yes'}</b>
                            {selection.skippedMealBreak &&
                                selection.skippedMealBreakComment && (
                                    <div>
                                        <em>
                                            {selection.skippedMealBreakComment}
                                        </em>
                                    </div>
                                )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
